import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useCustomMutation, useInvalidate, useUpdate } from '@refinedev/core'
import type {
  TPublisherLyricsApprovalDraftReviewSong,
  TPublisherRequest,
  TPublisherSong,
} from '@modules/types'
import {
  songChannel,
  TSongEventName,
  TRequestEventName,
} from '@publisher/modules/analytics/channels'
import { useAnalytics } from '@publisher/modules/analytics/hooks'
import { getSubmitError } from '../../../utils/getSubmitError'
import { approvalLyricsReducer, initialApprovalLyricsState } from './approvalLyricsReducer'

type UsePublisherApprovalLyricsParams = {
  requestId: number
  selectedRequest: TPublisherRequest
  apiUrl: string
  onDrawerClose: () => void
  onReviewSuccess: () => void
}

type TLyricDraftSong = TPublisherLyricsApprovalDraftReviewSong

const buildSongsFromOriginal = (
  selectedRequest: TPublisherRequest | undefined,
): TLyricDraftSong[] => {
  if (!selectedRequest?.songs) return []

  return selectedRequest.songs.map((song: TPublisherSong) => ({
    songId: song.id,
    isApproved: song.approvalDecision?.isApproved === 1 ? true : null,
    lyricsPublisherFee: song.lyricsPublisherFee ?? null,
  }))
}

export const usePublisherApprovalLyrics = ({
  requestId,
  selectedRequest,
  apiUrl,
  onDrawerClose,
  onReviewSuccess,
}: UsePublisherApprovalLyricsParams) => {
  const [state, dispatch] = useReducer(approvalLyricsReducer, initialApprovalLyricsState)
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})
  const { mutate: patchPublisherRequestDraft, isLoading } = useUpdate()
  const { mutate: sendReview, isPending: isSendingReview } = useCustomMutation()
  const invalidate = useInvalidate()
  const { channels } = useAnalytics()

  useEffect(() => {
    dispatch({ type: 'SYNC_FROM_REQUEST', payload: { selectedRequest } })
  }, [selectedRequest])

  const patchDraftReviewSongs = useCallback(
    (
      songs: TLyricDraftSong[],
      options?: { onSuccess?: () => void; onError?: () => void; onFinally?: () => void },
    ) => {
      patchPublisherRequestDraft(
        {
          resource: 'publisher-request',
          id: requestId,
          values: {
            draftReview: {
              type: selectedRequest.draftReview?.type || 1,
              songs,
            },
          },
          meta: {
            method: 'PATCH',
            disableNotification: true,
          },
        },
        {
          onSuccess: () => {
            options?.onSuccess?.()
            options?.onFinally?.()
          },
          onError: () => {
            options?.onError?.()
            options?.onFinally?.()
          },
        },
      )
    },
    [patchPublisherRequestDraft, requestId, selectedRequest.draftReview?.type],
  )

  const updateSong = useCallback(
    (
      songId: number,
      patch: Partial<TLyricDraftSong>,
      options?: { onSuccess?: () => void; onFinally?: () => void },
    ) => {
      const currentSongs = (selectedRequest.draftReview?.songs as TLyricDraftSong[] | undefined)
        ?.length
        ? (selectedRequest.draftReview?.songs as TLyricDraftSong[] | undefined) || []
        : buildSongsFromOriginal(selectedRequest)
      const updatedSongs = [...currentSongs]
      const idx = updatedSongs.findIndex(song => song.songId === songId)

      if (idx >= 0) {
        updatedSongs[idx] = { ...updatedSongs[idx], ...patch, songId }
      } else {
        updatedSongs.push({ songId, isApproved: null, ...patch })
      }

      patchDraftReviewSongs(updatedSongs, {
        onSuccess: options?.onSuccess,
        onFinally: options?.onFinally,
      })
    },
    [patchDraftReviewSongs, selectedRequest.draftReview?.songs],
  )

  const songsToSubmit = useMemo(() => {
    if (selectedRequest?.draftReview?.songs?.length) {
      return selectedRequest.draftReview.songs
    }
    return buildSongsFromOriginal(selectedRequest)
  }, [selectedRequest])

  const handleSendReview = useCallback(() => {
    channels.request.send({
      event: TRequestEventName.Review,
      requestId,
    })

    sendReview(
      {
        url: `${apiUrl}/publisher-request/${requestId}/review`,
        method: 'post',
        values: {
          songs: songsToSubmit,
        },
      },
      {
        onSuccess: () => {
          onDrawerClose()
          invalidate({ resource: 'publisher-request', id: requestId, invalidates: ['list'] })
          onReviewSuccess()
        },
        onError: error => {
          const serverErrors =
            (error as { data?: { errors?: Record<string, string[]> } })?.data?.errors || {}

          const { validationErrors: extractedErrors } = getSubmitError(
            error.message || 'An error occurred while submitting the review',
            selectedRequest?.songs,
            serverErrors,
          )
          setValidationErrors(extractedErrors)
        },
      },
    )
  }, [
    apiUrl,
    channels,
    invalidate,
    onDrawerClose,
    onReviewSuccess,
    requestId,
    selectedRequest?.songs,
    sendReview,
    songsToSubmit,
  ])

  const handleApproveSong = useCallback(
    (songId: number) => {
      songChannel.send({ event: TSongEventName.Approve, requestId, songId })
      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'approve' } })
      updateSong(
        songId,
        { isApproved: true, rejectReason: null },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [requestId, updateSong],
  )

  const handleRejectSong = useCallback(
    (songId: number, rejectReason: string) => {
      songChannel.send({ event: TSongEventName.Reject, requestId, songId, reason: rejectReason })
      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reject' } })
      updateSong(
        songId,
        { isApproved: false, rejectReason },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [requestId, updateSong],
  )

  const handleChangeStatus = useCallback(
    (songId: number) => {
      const key = String(songId)
      const originalSong = selectedRequest.songs?.find(song => song.id === songId)
      dispatch({
        type: 'SET_VALUE',
        payload: { key, value: (originalSong?.lyricsPublisherFee ?? 0).toString() },
      })
      dispatch({ type: 'SET_EDITING', payload: { key, value: false } })
      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reset' } })
      updateSong(
        songId,
        {
          isApproved: null,
          rejectReason: null,
          lyricsPublisherFee: null,
          isCounterOffered: false,
          counterOfferFee: undefined,
        },
        { onFinally: () => dispatch({ type: 'CLEAR_PROCESSING' }) },
      )
    },
    [selectedRequest.songs, updateSong],
  )

  const handleFeeEdit = useCallback(
    (songId: number) => {
      const key = String(songId)
      dispatch({ type: 'SET_EDITING', payload: { key, value: true } })
      const existingSong = (
        (selectedRequest.draftReview?.songs as TLyricDraftSong[] | undefined)?.length
          ? (selectedRequest.draftReview?.songs as TLyricDraftSong[] | undefined) || []
          : buildSongsFromOriginal(selectedRequest)
      ).find(song => song.songId === songId)
      if (existingSong?.lyricsPublisherFee != null) {
        dispatch({
          type: 'SET_VALUE',
          payload: { key, value: existingSong.lyricsPublisherFee.toString() },
        })
      } else if (existingSong?.counterOfferFee != null) {
        dispatch({
          type: 'SET_VALUE',
          payload: { key, value: existingSong.counterOfferFee.toString() },
        })
      }
    },
    [selectedRequest.draftReview?.songs],
  )

  const handleFeeChange = useCallback((songId: number, value: string) => {
    dispatch({ type: 'SET_VALUE', payload: { key: String(songId), value } })
  }, [])

  const handleFeeCancel = useCallback(
    (songId: number) => {
      const key = String(songId)
      dispatch({ type: 'SET_EDITING', payload: { key, value: false } })
      const originalSong = selectedRequest.songs?.find(song => song.id === songId)
      dispatch({
        type: 'SET_VALUE',
        payload: { key, value: (originalSong?.lyricsPublisherFee ?? 0).toString() },
      })
    },
    [selectedRequest.songs],
  )

  const handleFeeSave = useCallback(
    (songId: number) => {
      const key = String(songId)
      const value = state.feeValues[key]
      if (!value || Number.isNaN(Number(value))) {
        return
      }

      songChannel.send({ event: TSongEventName.Counteroffer, requestId, songId })
      dispatch({ type: 'SET_PROCESSING', payload: { songId, action: 'reset' } })
      updateSong(
        songId,
        {
          isApproved: false,
          rejectReason: 'Counteroffer',
          lyricsPublisherFee: Number(value),
          isCounterOffered: true,
          counterOfferFee: Number(value),
        },
        {
          onFinally: () => {
            dispatch({ type: 'SET_EDITING', payload: { key, value: false } })
            dispatch({ type: 'CLEAR_PROCESSING' })
          },
        },
      )
    },
    [requestId, state.feeValues, updateSong],
  )

  return {
    songsToSubmit,
    handleApproveSong,
    handleRejectSong,
    handleChangeStatus,
    isUpdatingSongStatus: isLoading,
    processingSongId: state.processingSongId,
    processingAction: state.processingAction,
    validationErrors,
    isSendingReview,
    handleSendReview,
    editingState: state.editingState,
    feeValues: state.feeValues,
    handleFeeEdit,
    handleFeeChange,
    handleFeeSave,
    handleFeeCancel,
  }
}
